/**
 * LoginV2 — V2 redesign of login page.
 * Route: /v2/login
 *
 * Design: centred card layout (no two-panel split),
 * forced light mode, V2 sidebar nav via V2Layout wrapper.
 * Clerk form preserved — only shell changes.
 */

import { SignIn, useAuth, useSignIn } from '@clerk/clerk-react';
import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router';
import { L } from '../components/V2Nav';
import { trackLogin } from '../lib/analytics';
// Design review: if already signed in we show a preview of the sign-in form
// (Clerk's <SignIn> auto-redirects when authenticated, preventing design review)

const clerkKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
const githubOAuthEnabled = import.meta.env.VITE_ENABLE_GITHUB_OAUTH === 'true';

const clerkAppearance = {
  layout: {
    unsafe_disableDevelopmentModeWarnings: true,
  },
  variables: {
    colorBackground: '#FAFAF7',
    colorInputBackground: '#F2F0EB',
    colorText: '#1A1916',
    colorTextSecondary: '#7A7669',
    colorPrimary: '#1B4FE0',
    colorDanger: '#DC2626',
    borderRadius: '8px',
    fontFamily: 'Inter, sans-serif',
  },
  elements: {
    // Let Clerk render its own card — don't try to override with transparent background
    // (broke in Clerk v5: caused double-card overlap)
    card: {
      border: `1px solid ${L.border}`,
      boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
      background: L.bgSurface,
    },
    socialButtonsBlockButton__github: githubOAuthEnabled
      ? { background: '#F6F8FA', border: '1px solid #D0D7DE', color: '#24292F' }
      : { display: 'none' },
    socialButtonsBlockButton__google: { background: '#FFFFFF', border: '1px solid #DADCE0', color: '#3C4043' },
    formButtonPrimary: 'font-semibold',
    footerActionLink: 'font-medium',
  },
};

// Static preview of the login form — shown when already signed in (Clerk would redirect)
function LoginPreview() {
  return (
    <div style={{ width: '100%' }}>
      {/* Social buttons */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
        {['Continue with Google', ...(githubOAuthEnabled ? ['Continue with GitHub'] : [])].map(label => (
          <div key={label} style={{ height: 38, border: `1px solid ${L.border}`, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: L.textMuted, background: L.bgSecondary }}>
            {label}
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        <div style={{ flex: 1, height: 1, background: L.border }} />
        <span style={{ fontSize: 12, color: L.textMuted }}>or</span>
        <div style={{ flex: 1, height: 1, background: L.border }} />
      </div>
      {/* Email field */}
      <div style={{ marginBottom: 12 }}>
        <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: L.textMuted, marginBottom: 5 }}>Email address</label>
        <div style={{ height: 38, border: `1px solid ${L.border}`, borderRadius: 8, background: L.bgSecondary }} />
      </div>
      {/* Password field */}
      <div style={{ marginBottom: 20 }}>
        <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: L.textMuted, marginBottom: 5 }}>Password</label>
        <div style={{ height: 38, border: `1px solid ${L.border}`, borderRadius: 8, background: L.bgSecondary }} />
      </div>
      {/* Sign in button */}
      <div style={{ height: 40, borderRadius: 8, background: L.signal, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 600, color: '#fff' }}>
        Continue
      </div>
      <p style={{ textAlign: 'center', marginTop: 12, fontSize: 12, color: L.textMuted }}>
        <span style={{ fontStyle: 'italic' }}>Design preview — </span>
        <a href="/login" style={{ color: L.signal, textDecoration: 'none' }}>use real login page</a>
      </p>
    </div>
  );
}

function LoginCard() {
  const { isSignedIn, isLoaded } = useAuth();
  const { signIn } = useSignIn();
  const [searchParams] = useSearchParams();
  const loginTracked = useRef(false);

  // redirect_url from /login?redirect_url=... (e.g. from /claim/:code auth guard)
  const redirectUrl = searchParams.get('redirect_url') ?? '/';

  // Fire a returning-user login event once when Clerk reports the sign-in
  // attempt completed (option a: Clerk useSignIn status hook). Distinct from
  // trackSignup, which only fires for first-time users post-onboarding.
  useEffect(() => {
    if (signIn?.status === 'complete' && !loginTracked.current) {
      loginTracked.current = true;
      trackLogin('clerk');
    }
  }, [signIn?.status]);

  if (!isLoaded) return <div style={{ minHeight: 300 }} />;

  // If signed in, show static preview (Clerk would auto-redirect otherwise)
  if (isSignedIn) return <LoginPreview />;

  return (
    <SignIn afterSignInUrl={redirectUrl} appearance={clerkAppearance} />
  );
}

export default function LoginV2() {
  // Force light mode
  useEffect(() => {
    const prev = document.documentElement.getAttribute('data-theme');
    document.documentElement.setAttribute('data-theme', 'light');
    return () => { document.documentElement.setAttribute('data-theme', prev ?? 'dark'); };
  }, []);

  return (
    <div style={{
      minHeight: '100vh',
      background: L.bgPrimary,
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      padding: '40px 24px',
    }}>
      {!clerkKey ? (
        <div style={{ textAlign: 'center', fontSize: 13, color: L.textMuted, padding: '20px 0' }}>
          Auth not configured.{' '}
          <code style={{ color: L.signal }}>VITE_CLERK_PUBLISHABLE_KEY</code> missing.
        </div>
      ) : (
        <LoginCard />
      )}
    </div>
  );
}
